import Buttons from "../../../../components/Buttons/Buttons";
import InputField from "../../../../components/Form/InputField";
import SelectField from "../../../../components/Form/SelectFields";

import { ReactComponent as AutogenerateIcon } from '../../../../assets/Icons/adornments/Generate.svg';
import { ACTrainModelDAGRequestModel, Dataset } from "../../../../api/models";
import { Dispatch, SetStateAction, MouseEvent, ChangeEvent, useState, useEffect } from "react";
import { classificationMethods, regressionMethods } from "../../../../utils/supplementalData";
import ConditionalSelectField from "../../../../components/Form/ConditionSelectField";
import TagsField from "../../../../components/Form/TagsField";
import { useGetSupportedSamplingMethodsModelTrainSupportedSamplingMethodsGet } from "../../../../api/generated/fastAPI";

const fingerprintOptions = [
    { value: 'ecfp', label: 'ECFP' },
    { value: 'fcfp', label: 'FCFP' },
    { value: 'maccs', label: 'MACCS' },
    { value: 'rdkfp', label: 'RDKit FP' },
    { value: 'rdkfp10', label: 'RDKit FP 10' },
    { value: 'atom_pair', label: 'Atom Pair' },
    { value: 'topological_torsion', label: 'Topological Torsion' },
    { value: 'fefp_90_gasteiger', label: 'Fragment Electrostatic 90 Gasteiger' },
    { value: 'fefp_95_gasteiger', label: 'Fragment Electrostatic 95 Gasteiger' },
    { value: 'molbart_encoder', label: 'MolBART Encoder' },
]

const bitOptions = [
    { value: 512, label: 512 },
    { value: 1024, label: 1024 },
    { value: 2048, label: 2048 }
];

const radiusOptions = [
    { value: 2, label: 2 },
    { value: 3, label: 3 },
    { value: 4, label: 4 }
]

const poolingOptions = [
    { value: 'all_pooling', label: 'All' },
    { value: 'sum_pooling', label: 'Sum' },
    { value: 'mean_pooling', label: 'Mean' },
    { value: 'max_pooling', label: 'Max' }
]

//max path

export default function SetTrainingParameters({
    form,
    setForm,
    chosenDS,
    fieldOptions,
    showSelect,
    selectedDSType,
    setSelectedDSType,
    modelOptions,
    setModelsOptions,
    samplingOptions,
    setSamplingOptions,
    isDcmMode,
    setIsDcmMode,
    dcmMethodOptions,
    selectedDcmMethod,
    setSelectedDcmMethod,
    dcmSourceOptions,
    selectedDcmSource,
    setSelectedDcmSource,
}: {
    form: ACTrainModelDAGRequestModel,
    setForm: Dispatch<SetStateAction<ACTrainModelDAGRequestModel>>,
    chosenDS: Dataset | null,
    fieldOptions: { value: string, label: string }[],
    showSelect: boolean,
    selectedDSType: 'continuous' | 'single-class' | 'multi-class' | null,
    setSelectedDSType: Dispatch<SetStateAction<'continuous' | 'single-class' | 'multi-class' | null>>,
    modelOptions: { value: string, label: string }[],
    setModelsOptions: Dispatch<SetStateAction<{ value: string, label: string }[]>>,
    samplingOptions: { value: string, label: string }[],
    setSamplingOptions: Dispatch<SetStateAction<{ value: string, label: string }[]>>,
    isDcmMode: boolean,
    setIsDcmMode: Dispatch<SetStateAction<boolean>>,
    dcmMethodOptions: { value: string, label: string }[],
    selectedDcmMethod: string,
    setSelectedDcmMethod: Dispatch<SetStateAction<string>>,
    dcmSourceOptions: { value: string, label: string }[],
    selectedDcmSource: string,
    setSelectedDcmSource: Dispatch<SetStateAction<string>>,
}) {
    const getSamplingMethodsMutation = useGetSupportedSamplingMethodsModelTrainSupportedSamplingMethodsGet();

    const [ selectedPooling, setSelectedPooling ] = useState<{ value: string, label: string }>({ value: 'all_pooling', label: 'All' });
    const [ selectedFingerprints, setSelectedFingerprints ] = useState<{ value: string, label: string }[]>([{ value: 'ecfp', label: 'ECFP' }]);
    const [ ecfpBitsAndRadius, setEcfpBitsAndRadius ] = useState<{
        selectedBits: { value: number, label: number },
        selectedRadius: { value: number, label: number }
    }>({
        selectedBits: { value: 1024, label: 1024 },
        selectedRadius: { value: 3, label: 3 },
    });

    const [ fcfpBitsAndRadius, setFcfpBitsAndRadius ] = useState<{
        selectedBits: { value: number, label: number },
        selectedRadius: { value: number, label: number }
    }>({
        selectedBits: { value: 1024, label: 1024 },
        selectedRadius: { value: 3, label: 3 },
    });


    useEffect(() => {
        if (getSamplingMethodsMutation.data) {
            const samplingMethods = getSamplingMethodsMutation.data as unknown as string[];
            setSamplingOptions([{ value: '', label: 'None' }].concat(samplingMethods.map(method => ({ value: method, label: method }))));
        }
    }, [getSamplingMethodsMutation.data, setSamplingOptions]);
    

    useEffect(() => {
        let fingerprintValues: string[] = [];

        selectedFingerprints.forEach(fingerprint => {
            if (fingerprint.value === 'ecfp') {
                fingerprintValues.push(`${fingerprint.value}${ecfpBitsAndRadius.selectedRadius.value * 2}_${ecfpBitsAndRadius.selectedBits.value}`)
            } else if (fingerprint.value === 'fcfp') {
                fingerprintValues.push(`${fingerprint.value}${fcfpBitsAndRadius.selectedRadius.value * 2}_${fcfpBitsAndRadius.selectedBits.value}`)
            } else if (fingerprint.value === 'molbart_encoder') {
                fingerprintValues.push(`${fingerprint.value}_${selectedPooling.value}`)
            } else {
                fingerprintValues.push(fingerprint.value)
            }
        });

        setForm(prevForm => ({
            ...prevForm,
            fp_type: fingerprintValues,
        }));

        //eslint-disable-next-line
    }, [
        ecfpBitsAndRadius,
        fcfpBitsAndRadius,
        selectedPooling,
        selectedFingerprints
    ])

    const autoGenerateName = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (chosenDS) {
            const name = chosenDS.name!;
            setForm({
                ...form,
                name,
            })
        }
    };

    const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        })
    };

    const handleMultiSelect = (e: any, name: string) => {
        setForm({
            ...form,
            [name]: Array.isArray(e) ? e.map(val => val.value) : e.value,
        })
    }

    const handleSelect = (e:any, name: string) => {
        setSelectedPooling(e)
    }

    const handleMultiSelectFingerprints = (e:any, name: string) => {
        setSelectedFingerprints(e);
    }

    const handleFingerprintParamSelect = (e:any, name: string) => {
        if (name.includes('ecfp')) {
            setEcfpBitsAndRadius({
                selectedBits: name.includes('bits') ? e : ecfpBitsAndRadius.selectedBits,
                selectedRadius: name.includes('radius') ? e : ecfpBitsAndRadius.selectedRadius,
            })
        } else if (name.includes('fcfp')) {
            setFcfpBitsAndRadius({
                selectedBits: name.includes('bits') ? e : fcfpBitsAndRadius.selectedBits,
                selectedRadius: name.includes('radius') ? e : fcfpBitsAndRadius.selectedRadius,
            })
        } else {
            setSelectedPooling(e);
        }
    };

    const handleActivityColumnSelect = (activityColValue:string) => {
            let methods: string[] = [];

            const allMethods = classificationMethods.concat(regressionMethods);
            let showSamplingMethods = false;


            if (chosenDS) {
                let dsType: 'continuous' | 'single-class' | 'multi-class' | null = null;
                const columnMetadata = chosenDS.dataset_metadata!.column_metadata;
                if (columnMetadata) {
                    const key = Object.keys(columnMetadata).find(key => key === activityColValue);
                    if (key) {
                        if (key.includes('single')) {
                            dsType = 'single-class'
                        } else if (key.includes('continuous')) {
                            dsType = 'continuous'
                        } else {
                            const value = columnMetadata[key];
                            if (value) {
                                if (value.data_type && value.data_type.includes('single')) {
                                    dsType = 'single-class'
                                } else if (value.data_type && value.data_type.includes('continuous')) {
                                    dsType = 'continuous'
                                } else if (value.data_type && value.data_type.includes('multi')) {
                                    dsType = 'multi-class'
                                }
                            }
                        }
                        
                    }
                };

                if (!dsType) {
                    const datasetTypes = chosenDS.dataset_metadata!.dataset_types!;
                    if (datasetTypes.length < 1) {
                        dsType = 'multi-class'
                    } else {
                        if (datasetTypes.includes('multi-class-label')) {
                            dsType = 'multi-class'
                        } else if (datasetTypes.includes('single-class-label')) {
                            if (datasetTypes.includes('continuous-value')) {
                                dsType = 'multi-class'
                            } else {
                                dsType = 'single-class'
                            }
                        } else {
                            dsType = 'continuous'
                        }
                    }

                };

                if (dsType) {
                    if (dsType === 'multi-class') {
                        methods = allMethods;
                    } else if (dsType === 'continuous') {
                        methods = regressionMethods;
                    } else {
                        methods = classificationMethods;
                    }

                    if (isDcmMode) {
                        methods = dcmMethodOptions.map((option) => option.value);
                    }

                    setModelsOptions(methods.map(method => ({ value: method, label: method })));
                    
                }

                setSelectedDSType(dsType);
                if (dsType === 'single-class') {
                    showSamplingMethods = true;
                }
            }

            setForm({
                ...form,
                dataset: {
                    ...form.dataset!,
                    y_col: activityColValue
                },
                models: methods,
                sampling_method: showSamplingMethods && !isDcmMode ? '' : undefined,

            });
    };

    const handleStructureColumnSelect = (structureColValue:string) => {
        setForm({
            ...form,
            dataset: {
                ...form.dataset!,
                structure_col: structureColValue
            }
        })
    };

    const handleDSParamSelect = (e:any, name: string) => {
        if (name === 'structure_col') {
            handleStructureColumnSelect(e.value);
        } else {
            handleActivityColumnSelect(e.value);
        }
    };

    const handleSamplingMethodSelection = (e:any, name: string) => {
        const value = e.value as string;
        setForm({
            ...form,
            sampling_method: value,
        })
    }

    const handleDcmModeSelection = (e:any) => {
        const enabled = e?.value === 'dcm';
        setIsDcmMode(enabled);

        if (enabled) {
            const dcmMethods = dcmMethodOptions.map((option) => option.value);
            setModelsOptions(dcmMethodOptions);
            setSelectedDcmMethod(dcmMethods[0]);
            setForm({
                ...form,
                models: [dcmMethods[0]],
                sampling_method: undefined,
            });
            return;
        }

        // Restore model options to dataset-type defaults when leaving DCM mode.
        handleActivityColumnSelect(form.dataset?.y_col ?? '');
    };

    const handleDcmMethodSelection = (e:any) => {
        const method = e?.value as string;
        setSelectedDcmMethod(method);
        if (!method) return;

        setForm({
            ...form,
            models: [method],
        });
    };

    const handleDcmSourceSelection = (e:any) => {
        const source = e?.value as string;
        if (!source) return;
        setSelectedDcmSource(source);
    };

    const handleAddTag = (category: string, tag: string) => {
        let addedTag = '';

        if (category === 'general') {
            addedTag = tag;
        } else {
            addedTag = `${category}::${tag}`
        }

        setForm({
            ...form,
            tags: form!.tags!.concat(addedTag)

        })
    }

    const handleRemoveTag = (index: number) => {
        setForm({
            ...form,
            tags: form!.tags!.filter((t, i) => i !== index)
        })
    }
    

    return (
        <>
            <div className="w-full flex justify-between items-end gap-4">
                <InputField 
                    label="Model Family Name"
                    type='text'
                    id='name'
                    name='name'
                    value={form.name as string}
                    isRequired
                    handleChange={handleInput}
                />
                <Buttons
                    id='autogenerate-btn'
                    type='secondary'
                    styling="solid"
                    label='Autogenerate'
                    startingAdornment={<AutogenerateIcon/>}
                    action={autoGenerateName}
                />
            </div>

            <div className="w-full">
                <ConditionalSelectField label='Fingerprints' showSelect={showSelect}>
                    <SelectField 
                        label="Fingerprints"
                        id='fingerprints'
                        name='fingerprints'
                        options={fingerprintOptions}
                        value={selectedFingerprints}
                        isRequired={true}
                        isCreatable={false}
                        isMulti={true}
                        handleChange={handleMultiSelectFingerprints}
                        menuPortalTarget={document.getElementById('train-model-popup')}
                    />
                </ConditionalSelectField>
            </div>

            {selectedFingerprints.map(f => f.value).includes('ecfp') && (
                <div className="w-full flex justify-between items-end gap-4">
                    <ConditionalSelectField label='ECFP Bits' showSelect={showSelect}>
                        <SelectField 
                            label="ECFP Bits"
                            id='ecfp_bits'
                            name='ecfp_bits'
                            options={bitOptions}
                            value={ecfpBitsAndRadius.selectedBits}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleFingerprintParamSelect}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                    <ConditionalSelectField label='ECFP Radius' showSelect={showSelect}>
                        <SelectField 
                            label="ECFP Radius"
                            id='ecfp_radius'
                            name='ecfp_radius'
                            options={radiusOptions}
                            value={ecfpBitsAndRadius.selectedRadius}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleFingerprintParamSelect}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )}

            {selectedFingerprints.map(f => f.value).includes('fcfp') && (
                <div className="w-full flex justify-between items-end gap-4">
                    <ConditionalSelectField label='FCFP Bits' showSelect={showSelect}>
                        <SelectField 
                            label="FCFP Bits"
                            id='fcfp_bits'
                            name='fcfp_bits'
                            options={bitOptions}
                            value={fcfpBitsAndRadius.selectedBits}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleFingerprintParamSelect}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                    <ConditionalSelectField label='FCFP Radius' showSelect={showSelect}>
                        <SelectField 
                            label="FCFP Radius"
                            id='fcfp_radius'
                            name='fcfp_radius'
                            options={radiusOptions}
                            value={fcfpBitsAndRadius.selectedRadius}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleFingerprintParamSelect}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )}

            {selectedFingerprints.map(f => f.value).includes('molbart_encoder') && (
                <div className="w-full">
                    <ConditionalSelectField label='Pooling Type' showSelect={showSelect}>
                        <SelectField 
                            label="Pooling Type"
                            id='pooling'
                            name='pooling'
                            options={poolingOptions}
                            value={selectedPooling}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleSelect}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )}


            {fieldOptions.length > 0 && (
                <div className="w-full flex justify-between items-end gap-4">
                    <ConditionalSelectField label='Structure Column' showSelect={showSelect}>
                        <SelectField 
                            label="Structure Column"
                            id='structure_col'
                            name='structure_col'
                            options={fieldOptions}
                            value={form.dataset!.structure_col ? { value: form.dataset!.structure_col!, label: form.dataset!.structure_col! } : null}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleDSParamSelect}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                    <ConditionalSelectField label='Activity Column' showSelect={showSelect}>
                        <SelectField 
                            label="Activity Column"
                            id='y_col'
                            name='y_col'
                            options={fieldOptions}
                            value={form.dataset!.y_col ? { value: form.dataset!.y_col!, label: form.dataset!.y_col! } : null}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleDSParamSelect}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )}

            <div className="w-full flex justify-between items-end gap-4">
                <ConditionalSelectField label='Training Mode' showSelect={showSelect}>
                    <SelectField
                        label="Training Mode"
                        id='training_mode'
                        name='training_mode'
                        options={[
                            { value: 'regular', label: 'Regular' },
                            { value: 'dcm', label: 'DCM' },
                        ]}
                        value={{ value: isDcmMode ? 'dcm' : 'regular', label: isDcmMode ? 'DCM' : 'Regular' }}
                        isRequired={true}
                        isCreatable={false}
                        isMulti={false}
                        handleChange={handleDcmModeSelection}
                        menuPortalTarget={document.getElementById('train-model-popup')}
                    />
                </ConditionalSelectField>

                {isDcmMode && (
                    <ConditionalSelectField label='DCM Method' showSelect={showSelect}>
                        <SelectField
                            label="DCM Method"
                            id='dcm_method'
                            name='dcm_method'
                            options={dcmMethodOptions}
                            value={{ value: selectedDcmMethod, label: selectedDcmMethod }}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleDcmMethodSelection}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                )}
            </div>

            {isDcmMode && (
                <div className="w-full">
                    <ConditionalSelectField label='OOD Source' showSelect={showSelect}>
                        <SelectField
                            label="OOD Source"
                            id='dcm_source'
                            name='dcm_source'
                            options={dcmSourceOptions}
                            value={{ value: selectedDcmSource, label: selectedDcmSource }}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleDcmSourceSelection}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )}

            {!isDcmMode && modelOptions.length > 0 && (
                <div className="w-full">
                    <ConditionalSelectField label='Models' showSelect={showSelect}>
                        <SelectField 
                            label="Models"
                            id='models'
                            name='models'
                            options={modelOptions}
                            value={form.models!.map(model => ({ value: model as string, label: model as string }))}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={true}
                            handleChange={handleMultiSelect}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )}

            {selectedDSType && selectedDSType === 'single-class' && samplingOptions.length > 0 && !isDcmMode && (
                <div className="w-full">
                    <ConditionalSelectField label="Sampling Method" showSelect={showSelect}>
                        <SelectField 
                            label="Sampling Method"
                            id='sampling_method'
                            name='sampling_method'
                            options={samplingOptions}
                            value={form.sampling_method === undefined ? null : samplingOptions.find(opt => opt.value === form.sampling_method)!}
                            isRequired={false}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleSamplingMethodSelection}
                            menuPortalTarget={document.getElementById('train-model-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )} 

            <TagsField 
                label="Model"
                tags={form.tags! as string[]}
                showSelect={showSelect}
                menuPortalTarget={document.getElementById('train-model-popup')}
                handleAddTag={handleAddTag}
                handleRemoveTag={handleRemoveTag}
            />
        </>
    )
}